import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, Wand2, FileDown, Box, FileSpreadsheet, FileText,
  Layers, Hammer, Cable, ClipboardList, ShieldCheck, Building2, Scaling, Calculator,
  Pencil, Eye, Save, X, GitCompare, Map, Zap, Droplets, Waves, Ruler, Package,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import PlanSVG from "@/components/PlanSVG";
import ZoomPan from "@/components/ZoomPan";
import InteractivePlanEditor from "@/components/InteractivePlanEditor";
import PlanExplanation from "@/components/PlanExplanation";
import Skeleton3D from "@/components/Skeleton3D";
import PlanDoctor from "@/components/PlanDoctor";
import StructuralSchedule from "@/components/StructuralSchedule";
import Plan3DViewer from "@/components/Plan3DViewer";
import { EngineeringDetailsButton } from "@/components/EngineeringDetails";
import { Button, Card, Badge, Select } from "@/components/ui";

const KIND_NAMES = {
  villa: ["فيلا", "Villa"], apartment: ["شقة", "Apartment"],
  studio_apartments: ["استوديوهات", "Studios"], apartments_floor: ["شقق كاملة", "Apartments"],
  office: ["مكاتب", "Office"], hotel: ["فندق", "Hotel"], shops: ["محلات", "Shops"],
  mall: ["مول", "Mall"], pool: ["مسبح", "Pool"], mosque: ["مسجد", "Mosque"],
  school: ["مدرسة", "School"], hospital: ["مستشفى", "Hospital"], factory: ["مصنع", "Factory"],
  duplex: ["دوبلكس", "Duplex"], commercial_hi: ["تجاري", "Commercial"],
  basement: ["بدروم", "Basement"], shop_house: ["محلات + سكن", "Shop-house"],
  imported: ["مستورد", "Imported"], manual_editor: ["يدوي", "Manual"],
};
function kindLabel(kind, isAr) {
  const e = KIND_NAMES[kind];
  return e ? (isAr ? e[0] : e[1]) : kind;
}


const LINKS = [
  ["/floor-plans", Layers, "المساقط", "Floor Plans"],
  ["/elevations", Building2, "الواجهات", "Elevations"],
  ["/section", Scaling, "القطاعات", "Section"],
  ["/foundation", Hammer, "الأساسات", "Foundation"],
  ["/materials", Calculator, "الكميات", "Materials"],
  ["/schedules", ClipboardList, "الجداول", "Schedules"],
  ["/takeoff", Calculator, "الحصر", "Take-off"],
  ["/mep-report", Cable, "MEP", "MEP"],
  ["/code-check", ShieldCheck, "فحص الكود", "Code Check"],
  ["/code-compare", GitCompare, "مقارنة الأكواد", "Code Compare"],
];

export default function Editor() {
  const { lang } = useAuth();
  const [wm, setWm] = useWorkingModel();
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(null);
  const [mode, setMode] = useState("view");
  const [draft, setDraft] = useState(null);
  const [bUsage, setBUsage] = useState("parking");
  const isAr = lang === "ar";

  const { data: codesData } = useDeliverable(() => api.editorListDesignCodes(), []);
  const designCodes = codesData?.items || [];
  const { data: sheetsData } = useDeliverable(() => api.editorListSheets(), []);

  const [analysis, setAnalysis] = useState(null);
  const [showFurniture, setShowFurniture] = useState(true);
  const [skeleton3d, setSkeleton3d] = useState(true);
  const [pdfScale, setPdfScale] = useState("100");
  useEffect(() => {
    const m = wm?.model;
    if (!m || !(m.walls || []).length) { setAnalysis(null); return; }
    let alive = true;
    api.editorAnalyzeLayout(m).then((a) => { if (alive) setAnalysis(a); }).catch(() => {});
    return () => { alive = false; };
  }, [wm?.model]);

  async function run(name, fn) {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); }
  }

  return (
    <RequireModel>
      {() => {
        const model = wm.model;
        const projectName = wm.title || "Plan";
        const designCode = wm.design_code || "SBC_304";

        const enterEdit = () => { setDraft(model); setMode("edit"); };
        const addBasement = () => run("addBasement", async () => {
          const xs = (model.walls || []).flatMap((w) => [w.p1.x, w.p2.x]);
          const ys = (model.walls || []).flatMap((w) => [w.p1.y, w.p2.y]);
          const gp = wm.generation_params || {};
          const land_w = gp.plot_w || (xs.length ? Math.max(...xs) - Math.min(...xs) : 20);
          const land_h = gp.plot_h || (ys.length ? Math.max(...ys) - Math.min(...ys) : 22);
          const name = (isAr ? "بدروم " : "Basement ") + projectName;
          const res = await api.editorGenerate("basement", { land_w, land_h, usage: bUsage, lang, project_name: name });
          setWm({ model: res.model, kind: "basement", floors: 1, slab_system: wm.slab_system || "solid", design_code: designCode, generation_params: { plot_w: land_w, plot_h: land_h }, title: name });
          setValidation(null); setMode("view");
          toast.success(isAr ? "تم توليد البدروم كمشروع مستقل" : "Basement generated as a standalone plan");
        });
        const saveEdit = () => run("save", async () => {
          setWm({ ...wm, model: draft });
          const v = await api.editorValidate(draft);
          setValidation(v);
          setMode("view");
          toast[v.ok ? "success" : "warning"](v.ok ? (isAr ? "حُفظ المخطط وهو سليم" : "Saved - plan is valid") : (isAr ? "حُفظ مع ملاحظات" : "Saved with issues"));
        });

        return (
          <div className="mx-auto max-w-7xl px-4 py-6">
            <PageHeader
              eyebrow={isAr ? "المحرر" : "Editor"}
              title={projectName}
              subtitle={isAr ? "عاين وحرّر المخطط بسحب الجدران، تحقق من الجودة، وصدّر الملفات الصناعية." : "View & edit by dragging walls, validate quality, and export industry files."}
              right={
                <div className="flex items-center gap-2">
                  <Badge tone="primary">{kindLabel(wm.kind, isAr)} · {wm.floors || 1} {isAr ? "طابق" : "fl"}</Badge>
                  {mode === "edit" ? (
                    <>
                      <Button size="sm" loading={busy === "save"} onClick={saveEdit} data-testid="save-edit-btn"><Save size={15} /> {isAr ? "حفظ" : "Save"}</Button>
                      <EngineeringDetailsButton isAr={isAr} />
                      <Button size="sm" variant="outline" onClick={() => setMode("view")} data-testid="cancel-edit-btn"><X size={15} /> {isAr ? "إلغاء" : "Cancel"}</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant={mode === "3d" ? "primary" : "outline"} onClick={() => setMode(mode === "3d" ? "view" : "3d")} data-testid="view-3d-btn"><Box size={15} /> 3D</Button>
                      <Button size="sm" variant={mode === "split" ? "primary" : "outline"} onClick={() => setMode(mode === "split" ? "view" : "split")} data-testid="split-view-btn">{isAr ? "2D+3D" : "2D+3D"}</Button>
                      <Button size="sm" variant="accent" onClick={enterEdit} data-testid="edit-mode-btn"><Pencil size={15} /> {isAr ? "تحرير" : "Edit"}</Button>
                    </>
                  )}
                </div>
              }
            />

            {mode === "edit" ? (
              <div className="space-y-4">
                <Card className="p-3">
                  <InteractivePlanEditor model={draft} onChange={setDraft} />
                  <p className="mt-2 text-xs text-muted-foreground">{isAr ? "أدوات: تحديد ثم اسحب جسم الجدار لتحريكه كاملاً، أو اسحب نقطة نهايته · جدار لرسم جدار بنقرتين · عمود لإضافة عمود · باب/نافذة/منور · اسحب العمود/الفتحة لتحريكه · حدّد عنصراً ثم Delete للحذف · Undo للتراجع · حدّد جداراً لإدخال طوله بدقة. النقاط القريبة تلتصق تلقائياً." : "Tools: Select then drag a wall BODY to move the whole wall, or drag its endpoint · Wall = 2-click new wall · Column adds a column · Door/Window/Vent · drag a column/opening to move it · select an element then Delete · Undo · select a wall for exact length. Nearby points auto-weld."}</p>
                </Card>
                <PlanDoctor model={draft} onApplyFixed={setDraft} lang={lang} />
                <StructuralSchedule model={draft} kind={wm.kind} floors={wm.floors || 1} slabSystem={wm.slab_system} designCode={wm.design_code} lang={lang} />
              </div>
            ) : mode === "split" ? (
              <div className="grid gap-4 lg:grid-cols-2" data-testid="split-view">
                <Card className="p-3">
                  <p className="mb-2 text-xs font-bold text-muted-foreground">2D</p>
                  <ZoomPan className="h-[520px]">
                    <div className="min-w-[600px] p-3"><PlanSVG model={model} w={820} h={620} showScale showDims furniture={analysis?.furniture} roomAreas={analysis?.room_areas} showFurniture={showFurniture} lang={lang} /></div>
                  </ZoomPan>
                </Card>
                <Card className="p-3">
                  <p className="mb-2 text-xs font-bold text-muted-foreground">3D · {isAr ? "هيكل" : "Skeleton"}</p>
                  <Skeleton3D model={model} floors={wm.floors || 1} wallHeight={3.2} />
                </Card>
              </div>
            ) : mode === "3d" ? (
              <Card className="p-3">
                <div className="mb-2 flex items-center justify-end gap-2">
                  <Button size="sm" variant={skeleton3d ? "primary" : "outline"} onClick={() => setSkeleton3d(true)} data-testid="skeleton-mode-btn">{isAr ? "هيكل (بلا أسطح)" : "Skeleton"}</Button>
                  <Button size="sm" variant={!skeleton3d ? "primary" : "outline"} onClick={() => setSkeleton3d(false)} data-testid="solid-mode-btn">{isAr ? "مصمت" : "Solid"}</Button>
                </div>
                {skeleton3d
                  ? <Skeleton3D model={model} floors={wm.floors || 1} wallHeight={3.2} />
                  : <Plan3DViewer model={model} floors={wm.floors || 1} wallHeight={3.2} />}
                <p className="mt-2 text-xs text-muted-foreground">{isAr ? "الهيكل: خطوط الجدران والأعمدة والبلاطات فقط بلا أسطح لتُبيّن البناء (عظم) · المصمت: نموذج ثلاثي الأبعاد كامل. للتصدير الكامل استخدم IFC." : "Skeleton: wall/column/slab edges only (no surfaces) to reveal the structure · Solid: full 3D model. Use IFC for full BIM export."}</p>
              </Card>
            ) : (
              <div className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
                <Card className="p-3">
                  <div className="mb-2 flex items-center justify-end">
                    <Button size="sm" variant={showFurniture ? "primary" : "outline"} onClick={() => setShowFurniture((v) => !v)} data-testid="toggle-furniture-btn">
                      {isAr ? (showFurniture ? "إخفاء الأثاث" : "إظهار الأثاث") : (showFurniture ? "Hide furniture" : "Show furniture")}
                    </Button>
                  </div>
                  <ZoomPan className="h-[560px]">
                    <div className="min-w-[700px] p-4"><PlanSVG model={model} w={900} h={680} showScale showLegend showDims furniture={analysis?.furniture} roomAreas={analysis?.room_areas} showFurniture={showFurniture} explain lang={lang} /></div>
                  </ZoomPan>
                  <p className="mt-2 text-xs font-semibold text-primary" data-testid="explain-hint">
                    {isAr ? "اضغط أي عنصر في المخطط لعرض رسمه وشرحه الكامل (قبل التحرير)" : "Tap any element in the plan to see its drawing + full explanation (before editing)"}
                  </p>
                </Card>

                <div className="space-y-4">
                  <PlanExplanation model={model} lang={lang} />
                  <Card>
                    <p className="mb-2 text-sm font-black">{isAr ? "كود التصميم" : "Design code"}</p>
                    <Select value={designCode} onChange={(e) => setWm({ ...wm, design_code: e.target.value })} data-testid="design-code-select">
                      {designCodes.map((c) => <option key={c.id} value={c.id}>{isAr ? c.name_ar : c.name_en}</option>)}
                    </Select>
                    {designCodes.find((c) => c.id === designCode) && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {designCodes.find((c) => c.id === designCode).concrete} · {designCodes.find((c) => c.id === designCode).steel}
                      </p>
                    )}
                  </Card>

                  {model.structural_meta && model.structural_meta.column_size_m && (
                    <Card>
                      <p className="mb-2 text-sm font-black">{isAr ? "الأعمدة (تقدير أولي)" : "Columns (preliminary)"}</p>
                      <div className="flex flex-wrap gap-2 text-xs" data-testid="structural-meta">
                        <span className="rounded-md bg-primary/10 px-2 py-1 font-bold text-primary">
                          {isAr ? "مقطع العمود" : "Column"}: {(model.structural_meta.column_size_m * 100).toFixed(0)}×{(model.structural_meta.column_size_m * 100).toFixed(0)} {isAr ? "سم" : "cm"}
                        </span>
                        <span className="rounded-md bg-primary/10 px-2 py-1 font-bold text-primary">
                          {isAr ? "أقصى بحر" : "Max bay"}: {model.structural_meta.max_bay_span_m} {isAr ? "م" : "m"}
                        </span>
                        <span className="rounded-md bg-muted px-2 py-1 font-bold">
                          {model.structural_meta.columns_required ? (isAr ? "هيكل خرساني" : "RC frame") : (isAr ? "جدران حاملة" : "Load-bearing")}
                        </span>
                      </div>
                      <p className="mt-2 text-[11px] leading-relaxed text-amber-600 dark:text-amber-400" data-testid="structural-disclaimer">
                        ⚠ {isAr
                          ? "تقدير أولي قائم على قواعد إرشادية للتصميم المبدئي وحصر الكميات فقط - يجب اعتماد المقاطع والتباعد والتسليح من مهندس إنشائي مرخّص بعد تحليل الأحمال وتقرير التربة."
                          : "Preliminary rule-based estimate for early design / BoQ only - column sizes, spacing and reinforcement must be verified by a licensed structural engineer after load analysis and a soil report."}
                      </p>
                    </Card>
                  )}

                  {model.layout_meta && model.layout_meta.grade && (
                    <Card>
                      <p className="mb-2 text-sm font-black">{isAr ? "كفاءة التوزيع الهندسي" : "Layout distribution"}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="layout-meta">
                        <span className={`rounded-md px-2 py-1 font-black ${model.layout_meta.grade === "excellent" ? "bg-green-500/15 text-green-600" : model.layout_meta.grade === "good" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
                          {isAr ? model.layout_meta.grade_ar : model.layout_meta.grade}
                        </span>
                        <span className="rounded-md bg-muted px-2 py-1 font-bold">{isAr ? "نسبة البناء" : "Coverage"}: {model.layout_meta.coverage_pct}%</span>
                        <span className="rounded-md bg-muted px-2 py-1 font-bold">{isAr ? "غرف" : "Rooms"}: {model.layout_meta.functional_rooms}</span>
                        <span className="rounded-md bg-muted px-2 py-1 font-bold">{isAr ? "ممرات/موزعات" : "Circulation"}: {model.layout_meta.circulation_spaces}</span>
                        <span className="rounded-md bg-muted px-2 py-1 font-bold">{isAr ? "مناور" : "Light-wells"}: {model.layout_meta.light_wells}</span>
                      </div>
                      {model.layout_meta.logical_distribution && (
                        <p className="mt-2 text-[11px] font-bold text-green-600 dark:text-green-400">✓ {isAr ? "توزيع منطقي: الوصول لكل الغرف عبر ممرات/موزعات + منور للغرف الداخلية" : "Logical distribution: every room reached via corridors + light-wells for interior rooms"}</p>
                      )}
                    </Card>
                  )}

                  {analysis?.circulation && (
                    <Card data-testid="circulation-card">
                      <p className="mb-2 text-sm font-black">{isAr ? "فحص التوزيع والحركة" : "Circulation check"}</p>
                      {analysis.circulation.ok ? (
                        <p className="flex items-center gap-2 rounded-md bg-green-500/15 px-2 py-1.5 text-xs font-black text-green-600 dark:text-green-400" data-testid="circulation-ok">
                          <CheckCircle2 size={15} /> {isAr ? `توزيع سليم: كل الغرف (${analysis.circulation.served_count}) تصل عبر ممر التوزيع` : `Sound: all ${analysis.circulation.served_count} rooms reach the corridor`}
                        </p>
                      ) : (
                        <div className="rounded-md bg-amber-500/15 px-2 py-1.5 text-xs font-bold text-amber-600 dark:text-amber-400" data-testid="circulation-warn">
                          <p className="flex items-center gap-2 font-black"><AlertTriangle size={15} /> {isAr ? "غرف تمريرية (لا تصل عبر ممر مباشر):" : "Pass-through rooms (no direct corridor access):"}</p>
                          <p className="mt-1">{analysis.circulation.passthrough.join("، ")}</p>
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                        <span className="rounded-md bg-muted px-2 py-1 font-bold">{isAr ? "ممرات" : "Corridors"}: {analysis.circulation.corridors}</span>
                        <span className="rounded-md bg-muted px-2 py-1 font-bold">{isAr ? "غرف مخدومة" : "Served"}: {analysis.circulation.served_count}/{analysis.circulation.room_count}</span>
                      </div>
                    </Card>
                  )}

                  <Card>
                    <p className="mb-3 text-sm font-black">{isAr ? "الجودة والتحسين" : "Quality & optimization"}</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" loading={busy === "validate"} onClick={() => run("validate", async () => {
                        const v = await api.editorValidate(model); setValidation(v);
                        toast[v.ok ? "success" : "warning"](v.ok ? (isAr ? "المخطط سليم" : "Plan is valid") : (isAr ? "توجد ملاحظات" : "Issues found"));
                      })} data-testid="editor-validate-btn"><CheckCircle2 size={15} /> {isAr ? "تحقق" : "Validate"}</Button>
                      <Button size="sm" variant="outline" loading={busy === "optimize"} onClick={() => run("optimize", async () => {
                        const r = await api.editorOptimizeApply(model);
                        if (r.applied) { setWm({ ...wm, model: r.model }); toast.success(isAr ? "تم تحسين الشبكة الإنشائية" : "Structural grid optimized"); }
                        else toast.info(isAr ? "الشبكة محسّنة بالفعل" : "Grid already optimal");
                      })} data-testid="editor-optimize-btn"><Wand2 size={15} /> {isAr ? "حسّن الأعمدة" : "Optimize grid"}</Button>
                    </div>
                    {validation && (
                      <div className="mt-3 space-y-1.5 rounded-lg bg-black/20 p-3 text-xs">
                        <div className="flex flex-wrap gap-3 font-bold">
                          <span>🧱 {validation.stats?.wall_count}</span>
                          <span>🚪 {validation.stats?.door_count}</span>
                          <span>🪟 {validation.stats?.window_count}</span>
                          <span>🏛️ {validation.stats?.column_count}</span>
                        </div>
                        {(validation.errors || []).slice(0, 5).map((er, i) => (
                          <div key={i} className="flex items-start gap-1.5 text-red-400"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {isAr ? er.message_ar : er.message_en || er.code}</div>
                        ))}
                        {validation.ok && <div className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 size={13} /> {isAr ? "لا توجد أخطاء هندسية" : "No geometric errors"}</div>}
                      </div>
                    )}
                  </Card>

                  <Card>
                    <p className="mb-3 text-sm font-black">{isAr ? "التصدير" : "Export"}</p>
                    <div className="mb-2 flex items-center gap-2">
                      <label className="text-xs font-bold text-muted-foreground">{isAr ? "مقياس رسم PDF" : "PDF scale"}</label>
                      <select
                        value={pdfScale}
                        onChange={(e) => setPdfScale(e.target.value)}
                        data-testid="pdf-scale-select"
                        className="flex-1 rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-bold text-foreground focus:border-primary focus:outline-none"
                      >
                        <option value="fit">{isAr ? "ملائم للصفحة" : "Fit to page"}</option>
                        <option value="50">1:50</option>
                        <option value="100">1:100</option>
                        <option value="200">1:200</option>
                        <option value="500">1:500</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button size="sm" variant="subtle" loading={busy === "dxf"} onClick={() => run("dxf", () => api.editorDownloadExport("dxf", model, projectName))} data-testid="export-dxf"><FileDown size={15} /> DXF</Button>
                      <Button size="sm" variant="subtle" loading={busy === "pdf"} onClick={() => run("pdf", () => api.editorDownloadExport("pdf", model, projectName, { scale_ratio: pdfScale === "fit" ? null : parseInt(pdfScale, 10) }))} data-testid="export-pdf"><FileText size={15} /> PDF</Button>
                      <Button size="sm" variant="subtle" loading={busy === "ifc"} onClick={() => run("ifc", () => api.editorExportIFC(model, projectName, wm.floors || 1))} data-testid="export-ifc"><Box size={15} /> IFC</Button>
                      <Button size="sm" variant="subtle" loading={busy === "boq"} onClick={() => run("boq", () => api.editorDownloadExport("boq", model, projectName))} data-testid="export-boq"><FileSpreadsheet size={15} /> BOQ</Button>
                      <Button size="sm" variant="subtle" loading={busy === "pack"} onClick={() => run("pack", () => api.editorDownloadExport("pdf-pack", model, projectName, { floors: wm.floors || 1, design_code: designCode, slab_system: wm.slab_system || "solid", has_basement: !!wm.has_basement, basement_depth_m: wm.basement_depth_m || 3.0 }))} data-testid="export-pack" className="col-span-2"><FileText size={15} /> {isAr ? "حزمة PDF الكاملة" : "Full PDF pack"}</Button>
                      <Button size="sm" variant="primary" loading={busy === "delivery"} onClick={() => run("delivery", () => api.editorDownloadExport("delivery-zip", model, projectName, { floors: wm.floors || 1, design_code: designCode, slab_system: wm.slab_system || "solid", has_basement: !!wm.has_basement, basement_depth_m: wm.basement_depth_m || 3.0, wall_height_m: 3.0 }))} data-testid="export-delivery-zip" className="col-span-2"><Package size={15} /> {isAr ? "حزمة التسليم الكاملة (ZIP): PDF + DXF + IFC + Excel" : "Complete Delivery Bundle (ZIP): PDF + DXF + IFC + Excel"}</Button>
                      <Button size="sm" variant="primary" loading={busy === "explain"} onClick={() => run("explain", () => api.editorExportPlanExplanation(model, projectName))} data-testid="export-plan-explanation" className="col-span-2"><FileText size={15} /> {isAr ? "شارك شرح المخطط (PDF)" : "Share plan explanation (PDF)"}</Button>
                    </div>
                    <p className="mb-2 mt-3 text-xs font-black text-muted-foreground">{isAr ? "لوحات مفردة" : "Individual sheets"}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {(() => {
                        const ICONS = { site: Map, architectural: Building2, foundation: Ruler, structural: Layers, electrical: Zap, plumbing: Droplets, pool: Waves, formwork: Hammer, reinforcement: Cable, "bar-schedule": ClipboardList, sections: Scaling, elevations: Scaling, joinery: Layers };
                        const FALLBACK = [
                          { id: "site", ar: "الموقع العام", en: "Site Plan" },
                          { id: "architectural", ar: "المعماري", en: "Architectural" },
                          { id: "foundation", ar: "الأساسات", en: "Foundation" },
                          { id: "structural", ar: "الإنشائي", en: "Structural" },
                          { id: "electrical", ar: "الكهرباء", en: "Electrical" },
                          { id: "plumbing", ar: "السباكة/الصرف", en: "Plumbing" },
                          { id: "pool", ar: "المسبح", en: "Pool" },
                          { id: "formwork", ar: "النجارة (الشدة)", en: "Formwork" },
                          { id: "reinforcement", ar: "حديد التسليح", en: "Reinforcement" },
                          { id: "bar-schedule", ar: "تقطيع الحديد", en: "Bar Schedule" },
                          { id: "sections", ar: "القطاعات", en: "Sections" },
                          { id: "elevations", ar: "الواجهات", en: "Elevations" },
                          { id: "joinery", ar: "الأبواب والنوافذ", en: "Doors & Windows" },
                        ];
                        const items = (sheetsData?.items && sheetsData.items.length) ? sheetsData.items : FALLBACK;
                        return items.map((s) => {
                          const Icon = ICONS[s.id] || FileText;
                          return (
                            <Button key={s.id} size="sm" variant="subtle" loading={busy === s.id}
                              onClick={() => run(s.id, () => api.editorDownloadSheet(s.id, model, projectName, { floors: wm.floors || 1, design_code: designCode, slab_system: wm.slab_system || "solid" }))}
                              data-testid={`export-sheet-${s.id}`}><Icon size={14} /> {isAr ? s.ar : s.en}</Button>
                          );
                        });
                      })()}
                    </div>
                  </Card>

                  <Card>
                    <p className="mb-1 text-sm font-black">{isAr ? "أضِف بدروماً لهذا المشروع" : "Add a basement to this project"}</p>
                    <p className="mb-3 text-[11px] text-muted-foreground">{isAr ? "يولّد قبواً بنفس أبعاد الأرض بجدران خرسانية استنادية وكامل مخططاته." : "Generates a basement for the same plot with RC retaining walls and its full plan set."}</p>
                    <div className="flex items-center gap-2">
                      <Select value={bUsage} onChange={(e) => setBUsage(e.target.value)} data-testid="basement-usage-select" className="flex-1">
                        <option value="parking">{isAr ? "مواقف سيارات" : "Parking"}</option>
                        <option value="storage">{isAr ? "تخزين" : "Storage"}</option>
                        <option value="apartment">{isAr ? "شقة سكنية" : "Apartment"}</option>
                      </Select>
                      <Button size="sm" variant="accent" loading={busy === "addBasement"} onClick={addBasement} data-testid="add-basement-btn">
                        <Building2 size={15} /> {isAr ? "توليد البدروم" : "Generate"}
                      </Button>
                    </div>
                  </Card>

                  <Card>
                    <p className="mb-3 text-sm font-black">{isAr ? "اللوحات الهندسية" : "Engineering sheets"}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {LINKS.map(([to, Icon, ar, en]) => (
                        <Link key={to} to={to} className="flex items-center gap-2 rounded-lg border border-border bg-card/50 px-3 py-2 text-xs font-bold hover:border-primary/50">
                          <Icon size={15} className="text-primary" /> {isAr ? ar : en}
                        </Link>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            )}
          </div>
        );
      }}
    </RequireModel>
  );
}
